// The AE's answers to a handoff waiting on them (spec C6, S15 batch 4), for every page that offers
// them: Accept (no confirm for one; the bulk bar asks once, "Accept 3"), and Send back with a required
// note and a confirm. Each ends in a toast with Undo, which puts the handoff back to waiting. Follow up
// on (batch 6a) shares the toast: its Undo puts the day before back. Finish handoff (batch 6b) returns
// it to the VA at a step; its Undo gives the renewal back to the AE. Close it myself (batch 6c) closes
// it, its Undo reopens it with the AE (unticking Done); a rewrite for the bank step uploads the new
// dec page first and goes back to the VA, its Undo as a return's. Pass to another AE (batch 7) asks
// for the AE and a note, then confirms; its Undo cancels the new AE's handoff, which gives the first
// one back. Take over (batch 7, decision 1) is a confirm only; its Undo puts the renewal back.
import { useRef, useState } from 'react';
import type { Api, ContinueAtStep, HandoffClose, RenewalRow, StepsView } from '../auth/api';
import { ConfirmPopup } from '../shell/ConfirmPopup';
import { Toast } from '../shell/Toast';
import {
  acceptedText,
  answerFailedText,
  type AnswerRow,
  answerUndoFailedText,
  answerUndoneText,
  bulkAcceptConfirm,
  bulkAcceptedText,
  closedText,
  closeFailedText,
  followUpFailedText,
  followUpSetText,
  followUpUndoFailedText,
  followUpUndoneText,
  passedText,
  passFailedText,
  passUndoneText,
  returnedText,
  returnFailedText,
  returnUndoneText,
  rewrittenText,
  sentBackText,
  takeOverConfirm,
  takeOverFailedText,
  takeOverUndoneText,
  tookOverText,
} from './ae-handoff-lines';
import { FinishHandoffDialog } from './FinishHandoff';
import { PassDialog } from './PassDialog';
import { SendBackDialog } from './SendBack';
import { uploadError } from './upload-lines';

type ToastState = { id: number; text: string; undo: (() => Promise<void>) | null };

/** What Take over needs of a row: who holds it and its open handoff, as the page saw them. */
export type TakeRow = Pick<RenewalRow, 'id' | 'insured' | 'handledBy' | 'handoff'>;

/** `onChanged` reads the page again after an answer or its Undo; `meId` is the signed-in AE. */
export function useHandoffAnswers(api: Api, onChanged: () => void, meId = '') {
  const [toast, setToast] = useState<ToastState | null>(null);
  const [sendingBack, setSendingBack] = useState<AnswerRow | null>(null);
  const [bulk, setBulk] = useState<AnswerRow[] | null>(null);
  const [finishing, setFinishing] = useState<{
    row: AnswerRow;
    steps: StepsView;
    initials: string;
  } | null>(null);
  const [carriers, setCarriers] = useState<string[]>([]);
  const [passing, setPassing] = useState<AnswerRow[] | null>(null);
  const [taking, setTaking] = useState<TakeRow[] | null>(null);
  const [busy, setBusy] = useState(false);
  // Two fast clicks land before the disabled state renders: only the first sends.
  const sending = useRef(false);
  const say = (text: string, undo: (() => Promise<void>) | null = null) =>
    setToast({ id: Date.now(), text, undo });

  /** The Undo of accepts and send backs: each handoff waits for the AE again. */
  const undoAnswers = (ids: string[]) => async () => {
    let failed = 0;
    for (const id of ids) {
      if (!(await api.undoHandoffAnswer(id)).ok) failed += 1;
    }
    say(failed === 0 ? answerUndoneText : answerUndoFailedText);
    onChanged();
  };

  /** Follow up on: true once set (or cleared), else why not, for the box to show. */
  const followUp = async (handoffId: string, on: string | null): Promise<true | string> => {
    if (sending.current) return 'Saving.';
    sending.current = true;
    const res = await api.followUpHandoff(handoffId, on).finally(() => {
      sending.current = false;
    });
    if (!res.ok) return followUpFailedText(res.status, res.error);
    // The Undo names this change, so it never takes back one made since in another tab.
    const at = res.body.change?.at ?? '';
    say(followUpSetText(on), async () => {
      const back = await api.undoFollowUp(handoffId, at);
      say(back.ok ? followUpUndoneText : followUpUndoFailedText);
      onChanged();
    });
    onChanged();
    return true;
  };

  const accept = async (r: AnswerRow) => {
    if (sending.current) return;
    sending.current = true;
    const res = await api.acceptHandoff(r.handoff.id).finally(() => {
      sending.current = false;
    });
    if (!res.ok) say(answerFailedText(res.status, res.error));
    else say(acceptedText(r.insured, res.body.renewal.status), undoAnswers([r.handoff.id]));
    onChanged();
  };

  const acceptAll = async (rows: AnswerRow[]) => {
    if (sending.current) return;
    sending.current = true;
    setBusy(true);
    const done: string[] = [];
    for (const r of rows) {
      if ((await api.acceptHandoff(r.handoff.id)).ok) done.push(r.handoff.id);
    }
    sending.current = false;
    setBusy(false);
    setBulk(null);
    say(
      bulkAcceptedText(done.length, rows.length - done.length),
      done.length ? undoAnswers(done) : null,
    );
    onChanged();
  };

  const sendBack = async (r: AnswerRow, note: string) => {
    if (sending.current) return false;
    sending.current = true;
    setBusy(true);
    const res = await api.sendBackHandoff(r.handoff.id, note).finally(() => {
      sending.current = false;
      setBusy(false);
    });
    if (!res.ok) return answerFailedText(res.status, res.error);
    setSendingBack(null);
    say(sentBackText(r.insured, r.handoff), undoAnswers([r.handoff.id]));
    onChanged();
    return true;
  };

  const giveBack = async (r: AnswerRow, note: string, step: ContinueAtStep, title: string) => {
    if (sending.current) return 'Saving.';
    sending.current = true;
    setBusy(true);
    const res = await api.returnHandoff(r.handoff.id, note, step).finally(() => {
      sending.current = false;
      setBusy(false);
    });
    if (!res.ok) return returnFailedText(res.status, res.error);
    setFinishing(null);
    say(returnedText(r.insured, r.handoff, title), async () => {
      const back = await api.undoHandoffAnswer(r.handoff.id);
      say(back.ok ? returnUndoneText : answerUndoFailedText);
      onChanged();
    });
    onChanged();
    return true as const;
  };

  const closeIt = async (
    r: AnswerRow,
    note: string,
    sheetComment: string,
    how: HandoffClose,
    decPage: File | null,
  ) => {
    if (sending.current) return 'Saving.';
    sending.current = true;
    setBusy(true);
    try {
      // The new carrier's dec page goes on step 1 first; the close names it, and the server checks
      // it is the renewal's dec page now (a PDF already there is found again, not copied).
      let sent = how;
      if (decPage && how.outcome === 'rewritten') {
        const up = await api.uploadDocument(r.id, decPage, decPage.name, 'dec_page');
        if (!up.ok) return uploadError(up.status, up.error ?? '');
        sent = { ...how, decPageId: up.body.documentId };
      }
      const res = await api.closeHandoff(r.handoff.id, note, sheetComment, sent);
      if (!res.ok) return closeFailedText(res.status, res.error);
    } finally {
      sending.current = false;
      setBusy(false);
    }
    setFinishing(null);
    const back = how.outcome === 'rewritten' && how.bank;
    say(
      back ? rewrittenText(r.insured, r.handoff) : closedText(r.insured, how.outcome),
      async () => {
        const undone = back
          ? await api.undoHandoffAnswer(r.handoff.id)
          : await api.undoStep(r.id, 'done');
        say(undone.ok ? returnUndoneText : answerUndoFailedText);
        onChanged();
      },
    );
    onChanged();
    return true as const;
  };

  const passAll = async (rows: AnswerRow[], ae: { id: string; name: string }, note: string) => {
    if (sending.current) return 'Saving.';
    sending.current = true;
    setBusy(true);
    const next: string[] = [];
    let failed: string | null = null;
    try {
      // One request each; a handoff that changed since the list was read is skipped.
      for (const r of rows) {
        const res = await api.passHandoff(r.handoff.id, ae.id, note);
        if (res.ok) {
          if (res.body.next) next.push(res.body.next.id);
        } else failed = passFailedText(res.status, res.error);
      }
    } finally {
      sending.current = false;
      setBusy(false);
    }
    // One alone that failed stays in the dialog with why.
    if (rows.length === 1 && failed) return failed;
    setPassing(null);
    say(
      passedText(rows, ae.name, next.length),
      next.length
        ? async () => {
            let bad = 0;
            for (const id of next) if (!(await api.cancelHandoff(id)).ok) bad += 1;
            say(bad === 0 ? passUndoneText : answerUndoFailedText);
            onChanged();
          }
        : null,
    );
    onChanged();
    return true as const;
  };

  const takeAll = async (rows: TakeRow[]) => {
    if (sending.current) return;
    sending.current = true;
    setBusy(true);
    const taken: string[] = [];
    let failed: string | null = null;
    try {
      for (const r of rows) {
        const res = await api.createHandoff(r.id, {
          kind: 'take_over',
          from: r.handledBy?.id ?? null,
        });
        if (res.ok) taken.push(res.body.handoff.id);
        else failed = takeOverFailedText(res.status, res.error);
      }
    } finally {
      sending.current = false;
      setBusy(false);
    }
    setTaking(null);
    say(
      rows.length === 1 && failed ? failed : tookOverText(rows, taken.length),
      taken.length
        ? async () => {
            let bad = 0;
            for (const id of taken) if (!(await api.undoHandoffAnswer(id)).ok) bad += 1;
            say(bad === 0 ? takeOverUndoneText : answerUndoFailedText);
            onChanged();
          }
        : null,
    );
    onChanged();
  };

  const element = (
    <>
      {passing && (
        <PassDialog
          api={api}
          rows={passing}
          meId={meId}
          busy={busy}
          onClose={() => setPassing(null)}
          onPass={(ae, note) => passAll(passing, ae, note)}
        />
      )}
      {taking && (
        <ConfirmPopup
          {...takeOverConfirm(taking, meId)}
          action={busy ? 'Taking over' : 'Take over'}
          busy={busy}
          onConfirm={() => void takeAll(taking)}
          onCancel={() => setTaking(null)}
        />
      )}
      {finishing && (
        <FinishHandoffDialog
          row={finishing.row}
          steps={finishing.steps}
          initials={finishing.initials}
          carriers={carriers}
          busy={busy}
          onClose={() => setFinishing(null)}
          onReturn={(note, step, title) => giveBack(finishing.row, note, step, title)}
          onCloseIt={(note, sheet, how, dec) => closeIt(finishing.row, note, sheet, how, dec)}
        />
      )}
      {sendingBack && (
        <SendBackDialog
          row={sendingBack}
          busy={busy}
          onClose={() => setSendingBack(null)}
          onSend={(note) => sendBack(sendingBack, note)}
        />
      )}
      {bulk && (
        <ConfirmPopup
          {...bulkAcceptConfirm(bulk.length)}
          action={busy ? 'Accepting' : bulkAcceptConfirm(bulk.length).action}
          busy={busy}
          onConfirm={() => void acceptAll(bulk)}
          onCancel={() => setBulk(null)}
        />
      )}
      {toast && (
        <Toast
          key={toast.id}
          text={toast.text}
          action={toast.undo ? { label: 'Undo', onClick: () => void toast.undo!() } : undefined}
          shownMs={toast.undo ? 8000 : undefined}
        />
      )}
    </>
  );

  return {
    accept: (r: AnswerRow) => void accept(r),
    acceptMany: (rows: AnswerRow[]) => setBulk(rows),
    sendBack: (r: AnswerRow) => setSendingBack(r),
    followUp,
    /** Pass to another AE, one or several handoffs on this AE (waiting or with them). */
    pass: (rows: AnswerRow[]) => rows.length > 0 && setPassing(rows),
    /** Take over (decision 1): a confirm, then each one is the AE's. */
    takeOver: (rows: TakeRow[]) => rows.length > 0 && setTaking(rows),
    /** Finish handoff, for the AE an accepted handoff is with; `steps` as the page read them. */
    finish: (row: AnswerRow, steps: StepsView, initials: string) => {
      setFinishing({ row, steps, initials });
      // The New carrier list, for a rewrite; read while the dialog opens.
      void api
        .handoffCarriers()
        .then(setCarriers)
        .catch(() => setCarriers([]));
    },
    element,
  };
}
